
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { usePrayerChoiceAnalytics } from '@/hooks/usePrayerChoiceAnalytics';
import { usePrayerCompanionAccess } from '@/hooks/usePrayerCompanionAccess';
import { usePrayerCompanionUsage } from '@/hooks/usePrayerCompanionUsage';
import type { ChoiceContext, PrayerContext, BibleVerseContext, getContextId, getContextTitle } from '@/types/prayerChoice';
import type { PrayerCompanionMode } from '@/types/prayerCompanion.types';

// Simple store for current verse context that AI assistant can access
let currentBibleVerseContext: BibleVerseContext | null = null;

export function setCurrentBibleVerseContext(context: BibleVerseContext | null) {
  currentBibleVerseContext = context;
}

export function getCurrentBibleVerseContext(): BibleVerseContext | null {
  return currentBibleVerseContext;
}

interface PrayerChoiceContextType {
  // State
  isOpen: boolean;
  choiceContext?: ChoiceContext;
  isAiAssistantOpen: boolean;
  
  // Companion state
  isCompanionOpen: boolean;
  companionMode: PrayerCompanionMode;
  
  // Actions
  openChoiceDialog: (context?: ChoiceContext, sourceContext?: string) => void;
  closeChoiceDialog: () => void;
  handleINeedPrayer: () => Promise<void>;
  handleIWantToPray: () => Promise<void>;
  closeAiAssistant: () => void;
  
  // New action for opening AI Assistant independently
  openAiAssistant: () => void;
  
  // Companion actions
  openCompanion: (mode?: PrayerCompanionMode) => void;
  closeCompanion: () => void;
  setCompanionMode: (mode: PrayerCompanionMode) => void;
}

const PrayerChoiceContext = createContext<PrayerChoiceContextType | undefined>(undefined);

export function PrayerChoiceProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [choiceContext, setChoiceContext] = useState<ChoiceContext | undefined>(undefined);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState<boolean>(false);
  
  // Companion state
  const [isCompanionOpen, setIsCompanionOpen] = useState<boolean>(false);
  const [companionModeState, setCompanionModeState] = useState<PrayerCompanionMode>('cards');
  
  // Wrapped setCompanionMode with debug logging
  const setCompanionMode = (mode: PrayerCompanionMode) => {
    console.log('[PrayerChoiceContext] setCompanionMode called with:', mode);
    console.log('[PrayerChoiceContext] Current companionMode before change:', companionModeState);
    console.log('[PrayerChoiceContext] Stack trace:', new Error().stack);
    setCompanionModeState(mode);
    console.log('[PrayerChoiceContext] companionMode changed to:', mode);
  };
  
  const companionMode = companionModeState;
  
  const analytics = usePrayerChoiceAnalytics();
  const navigate = useNavigate();
  const location = useLocation();


  // Access control hooks (only call at top level)
  const { canInteractWithMode, getAccessDeniedReason } = usePrayerCompanionAccess();
  const { incrementUsage } = usePrayerCompanionUsage();

  const openChoiceDialog = (context?: ChoiceContext, sourceContext = 'unknown') => {
    console.log('Global: Opening choice dialog with context:', context);
    
    // Close any open modals first
    setIsAiAssistantOpen(false);
    setIsCompanionOpen(false);
    
    setChoiceContext(context);
    setIsOpen(true);
    
    if (context) {
      // Set Bible verse context for AI assistant if applicable
      if ('type' in context && context.type === 'bible_verse') {
        setCurrentBibleVerseContext(context);
      } else {
        setCurrentBibleVerseContext(null);
      }
      
      // For Bible verses, use reference as ID; for prayers, use existing id
      const contextId = 'type' in context && context.type === 'bible_verse' ? context.reference : (context as PrayerContext).id;
      analytics.trackChoiceDialogOpened(contextId, sourceContext);
    }
  };

  const closeChoiceDialog = () => {
    console.log('Global: Closing choice dialog');
    setIsOpen(false);
    setChoiceContext(undefined);
    // Clear Bible verse context when closing
    setCurrentBibleVerseContext(null);
  };

  const handleINeedPrayer = async () => {
    console.log('Global: handleINeedPrayer called with context:', choiceContext);
    
    if (choiceContext) {
      // Get the appropriate ID for tracking
      const contextId = 'type' in choiceContext && choiceContext.type === 'bible_verse' ? choiceContext.reference : (choiceContext as PrayerContext).id;
      
      // Track the choice selection
      analytics.trackChoiceSelected(contextId, 'i_need_prayer');

      // Record the choice action
      const choiceActionId = await analytics.recordChoiceAction(
        contextId, 
        'i_need_prayer', 
        'ai_assistant'
      );

      console.log('Global: Choice action recorded with ID:', choiceActionId);
    }

    // Just handle analytics - don't open anything
    // The PrayerChoiceDialog will handle opening the AI Assistant directly
  };

  const handleIWantToPray = async () => {
    console.log('Global: handleIWantToPray called with context:', choiceContext);
    
    if (choiceContext) {
      // Get the appropriate ID for tracking
      const contextId = 'type' in choiceContext && choiceContext.type === 'bible_verse' ? choiceContext.reference : (choiceContext as PrayerContext).id;
      
      // Track the choice selection
      analytics.trackChoiceSelected(contextId, 'i_want_to_pray');

      // Record the choice action
      const choiceActionId = await analytics.recordChoiceAction(
        contextId, 
        'i_want_to_pray', 
        'manual_prayer'
      );

      if (choiceActionId) {
        await analytics.recordPrayedFor(contextId, choiceActionId, 'manual');
      }
      
      console.log('Global: Choice action recorded with ID:', choiceActionId);
    }

    console.log('Global: Closing choice dialog and navigating to share prayer page');
    closeChoiceDialog();
    
    // Track manual prayer completion
    if (choiceContext) {
      const contextId = 'type' in choiceContext && choiceContext.type === 'bible_verse' ? choiceContext.reference : (choiceContext as PrayerContext).id;
      analytics.trackMethodSelected(contextId, 'manual_prayer');
    }
    
    // Navigate to the share prayer page instead of opening modal
    console.log('Global: Navigating to /share-prayer');
    navigate('/share-prayer');
  };

  const closeAiAssistant = () => {
    console.log('Global: Closing AI assistant');
    setIsAiAssistantOpen(false);
  };

  // Function to open AI Assistant independently
  const openAiAssistant = () => {
    console.log('Global: Opening AI Assistant independently');
    setIsAiAssistantOpen(true);
    // Close other modals
    setIsOpen(false);
    setIsCompanionOpen(false);
  };

  // Companion actions
  const openCompanion = async (mode: PrayerCompanionMode = 'cards') => {
    console.log('🎯 Global: Opening companion in mode:', mode);
    console.log('🎯 Global: Current companion state - isOpen:', isCompanionOpen, 'mode:', companionMode);
    
    // Check access and handle gracefully
    const reason = getAccessDeniedReason(mode);
    console.log('🎯 Global: Access check result for', mode, 'mode:', reason);
    
    if (reason === 'NO_ACCESS') {
      console.log('🎯 Global: Access denied - NO_ACCESS');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('🎯 Global: User not authenticated, showing sign in toast');
        toast.error(`Sign in required - Please sign in to access ${mode} mode. It's free to get started!`);
      } else {
        console.log('🎯 Global: User authenticated but lacks premium access, showing upgrade toast');
        toast.error(`Premium feature - Upgrade to premium to unlock ${mode} mode and enjoy unlimited access.`);
      }
      return;
    }
    
    if (reason === 'DAILY_LIMIT') {
      console.log('🎯 Global: Access denied - DAILY_LIMIT reached for', mode, 'mode');
      toast.warning(`Daily limit reached - You've reached your daily limit for ${mode} mode. Upgrade to premium for unlimited access or try again tomorrow.`);
      return;
    }

    console.log('🎯 Global: Access granted for', mode, 'mode, proceeding to open companion');
    
    // For authenticated users, track usage before opening
    // For unauthenticated users with access to free features, skip tracking
    const { data: { user } } = await supabase.auth.getUser();
    console.log('🎯 Global: User check result:', user ? 'authenticated' : 'anonymous');
    
    if (user?.id) {
      console.log('🎯 Global: Incrementing usage for authenticated user');
      const success = await incrementUsage(mode);
      if (!success) {
        console.error('🎯 Global: Failed to track companion usage');
        return;
      }
      console.log('🎯 Global: Usage incremented successfully');
    } else {
      console.log('🎯 Unauthenticated user accessing free feature - skipping usage tracking');
    }

    console.log('🎯 Global: Current isCompanionOpen state before change:', isCompanionOpen);
    setIsCompanionOpen(true);
    setCompanionMode(mode);
    console.log('🎯 Global: Set isCompanionOpen to true, mode to:', mode);
    
    // Close other modals
    setIsOpen(false);
    setIsAiAssistantOpen(false);
    
    console.log('🎯 Global: Companion should now be visible');
  };

  const closeCompanion = () => {
    console.log('[PrayerChoiceContext] closeCompanion called');
    console.log('[PrayerChoiceContext] Current companionMode before closing:', companionModeState);
    setIsCompanionOpen(false);
    setCompanionMode('cards');
    console.log('[PrayerChoiceContext] Companion closed and mode reset to cards');
  };

  // Auto-close all modals when navigating to a new route
  useEffect(() => {
    console.log('Route changed, auto-closing Prayer Companion modals');
    closeCompanion();
    closeAiAssistant();
    closeChoiceDialog();
  }, [location.pathname]);

  console.log('Global PrayerChoiceContext state:', {
    isOpen,
    isAiAssistantOpen,
    isCompanionOpen,
    companionMode,
    choiceContext: choiceContext ? ('type' in choiceContext && choiceContext.type === 'bible_verse' ? `Bible: ${choiceContext.reference}` : (choiceContext as PrayerContext).title) : 'none'
  });

  const value: PrayerChoiceContextType = {
    isOpen,
    choiceContext,
    isAiAssistantOpen,
    isCompanionOpen,
    companionMode,
    openChoiceDialog,
    closeChoiceDialog,
    handleINeedPrayer,
    handleIWantToPray,
    closeAiAssistant,
    openAiAssistant,
    openCompanion,
    closeCompanion,
    setCompanionMode
  };

  return (
    <PrayerChoiceContext.Provider value={value}>
      {children}
    </PrayerChoiceContext.Provider>
  );
}

export function usePrayerChoice() {
  const context = useContext(PrayerChoiceContext);
  if (context === undefined) {
    throw new Error('usePrayerChoice must be used within a PrayerChoiceProvider');
  }
  return context;
}

